import React, { useState } from 'react';
import {
  DialogActions,
  Button,
  Checkbox,
} from '@mui/material';
import { capitalizeName } from '../../../common/utils/firstCharUpperCase';
import {
  DialogContainer,
  DialogHeader,
  DialogTitle,
  DialogText,
} from '../shared';

interface InterviewWelcomeDialogProps {
  open: boolean;
  onStart: () => void;
  candidateName?: string;
}

const InterviewWelcomeDialog: React.FC<InterviewWelcomeDialogProps> = ({
  open,
  onStart,
  candidateName = 'Candidate',
}) => {
  const [dataConsentAgreed, setDataConsentAgreed] = useState(false);

  return (
    <DialogContainer open={open}>
      <DialogHeader variant="interview" />

      {/* Scrollable Content Area */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
        }}
      >
        {/* Interview Info Section */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid #F3F4F6',
            borderBottom: '1px solid #F3F4F6',
            background: 'rgba(0, 0, 0, 0.00)',
          }}
        >
          <DialogTitle size="lg">
            Interview with {capitalizeName(candidateName)}
          </DialogTitle>
          <DialogText size="xs">
            This interview will be conducted using an AI-powered digital interviewer.
          </DialogText>
          <DialogText size="xs">
            In order to proceed with this interview, Mubadala will need to process your personal data generated from your recorded responses in order to evaluate your responses.
          </DialogText>
          <DialogText size="xs">
            You may withdraw your consent at any time, however, please note that if you withdraw your consent prior to Mubadala providing its feedback to you, Mubadala will not be able to continue with the recruitment process.
          </DialogText>
          <DialogText size="xs">
            For more information on our privacy practices, and how your data may be processed and how to withdraw your consent, please refer to our{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                opacity: 0.8,
                color: '#171C27',
                fontFamily: 'Interstate',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.6px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Candidate Privacy Notice
            </a>
            .
          </DialogText>
          <DialogText size="xs" style={{ marginBottom: 0 }}>
            Please keep this tab open for the entire duration of the interview. Do not refresh, reuse, or share this link. This link can only be used once and cannot be opened in another browser or device.
          </DialogText>
        </div>

        {/* Interview Consent */}
        <div
          style={{
            padding: '16px 24px',
            borderBottom: '1px solid #F3F4F6',
            background: 'rgba(0, 0, 0, 0.00)',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <Checkbox
              checked={dataConsentAgreed}
              onChange={(e) => setDataConsentAgreed(e.target.checked)}
              sx={{
                padding: 0,
                marginRight: '8px',
                marginTop: '2px',
                color: '#9CA3AF',
                '&.Mui-checked': {
                  color: '#171C27',
                },
              }}
            />
            <span
              style={{
                color: '#63615C',
                fontFamily: 'Interstate',
                fontSize: '12px',
                fontWeight: 500,
                letterSpacing: '0.6px',
              }}
            >
              I consent to the processing of my personal data and to the cross-border transfer of my personal data and special categories of personal data, as set out in the{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  opacity: 0.8,
                  color: '#171C27',
                  fontFamily: 'Interstate',
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.6px',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                Candidate Privacy Notice
              </a>
              .
            </span>
          </div>
        </div>
      </div>

      <DialogActions sx={{ padding: '24px', flexShrink: 0 }}>
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={!dataConsentAgreed}
          onClick={onStart}
          sx={{
            borderRadius: '6px',
            backgroundColor: '#63615C',
            boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.00), 0 0 0 0 rgba(0, 0, 0, 0.00), 0 1px 19px 0 rgba(17, 24, 39, 0.15)',
            textTransform: 'none',
            padding: '12px',
            '&:hover': {
              backgroundColor: '#252d33',
            },
            '&.Mui-disabled': {
              backgroundColor: '#9CA3AF',
              color: '#FFFFFF',
            },
          }}
        >
          Start Interview
        </Button>
      </DialogActions>
    </DialogContainer>
  );
};

export default InterviewWelcomeDialog;
